/*
 * Утилиты для снаппинга элементов на сцене Konva
 * https://konvajs.org/docs/sandbox/Objects_Snapping.html
 */
import Konva from 'konva';
import { getVisibleElementsOnScreenSync, screenToWorld } from './stageUtils';
// ============================================================================
// КОНСТАНТЫ
// ============================================================================
/** Дополнительное удлинение линии за пределы базовой длины */
const LINE_EXTENSION = 280;
/** Точность сравнения координат при поиске донора */
const COORDINATE_TOLERANCE = 0.1;
// ============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ============================================================================
/**
 * Вычисляет начальную координату линии на основе типа снаппинга
 *
 * @param referencePoint - Точка отсчета (центр элемента-донора)
 * @param snap - Тип снаппинга (start/center/end)
 * @param halfSize - Половина размера элемента (width/2 для горизонтали, height/2 для вертикали)
 * @returns Начальная координата линии
 */
const getLineStartCoordinate = (referencePoint, snap, halfSize) => {
    switch (snap) {
        case 'start':
            return referencePoint - halfSize;
        case 'end':
            return referencePoint + halfSize;
        case 'center':
        default:
            return referencePoint;
    }
};
/**
 * Создает координаты направляющей линии
 *
 * Линия начинается от точки снаппинга на элементе-доноре и имеет фиксированную длину,
 * направлена в сторону перемещаемого элемента.
 *
 * @param referencePoint - Точка снаппинга на доноре (в мировых координатах)
 * @param targetPoint - Точка на перемещаемом элементе (в мировых координатах)
 * @param orientation - Ориентация линии (V - вертикальная, H - горизонтальная)
 * @param lineLength - Базовая длина линии (в мировых координатах)
 * @param snap - Тип снаппинга (start/center/end)
 * @param donorSize - Размеры элемента-донора (в мировых координатах)
 * @returns Координаты начала и конца линии
 */
const buildGuideLineCoordinates = (referencePoint, targetPoint, orientation, lineLength, snap, donorSize) => {
    if (orientation === 'H') {
        // Горизонтальная линия: Y одинаковый, X меняется
        const donorHalfWidth = donorSize ? donorSize.width / 2 : 0;
        const isTargetRight = targetPoint.x >= referencePoint.x;
        const startX = getLineStartCoordinate(referencePoint.x, snap, donorHalfWidth);
        const direction = isTargetRight ? 1 : -1;
        const endX = startX + (lineLength + LINE_EXTENSION) * direction;
        return {
            startX,
            startY: referencePoint.y,
            endX,
            endY: referencePoint.y,
        };
    }
    // Вертикальная линия: X одинаковый, Y меняется
    const donorHalfHeight = donorSize ? donorSize.height / 2 : 0;
    const isTargetBelow = targetPoint.y >= referencePoint.y;
    const startY = getLineStartCoordinate(referencePoint.y, snap, donorHalfHeight);
    const direction = isTargetBelow ? 1 : -1;
    const endY = startY + (lineLength + LINE_EXTENSION) * direction;
    return {
        startX: referencePoint.x,
        startY,
        endX: referencePoint.x,
        endY,
    };
};
/**
 * Безопасно получает bounding box узла
 *
 * @param node - Узел Konva
 * @returns Bounding box или null, если узел не поддерживает getClientRect
 */
const getNodeClientRect = (node) => {
    if (!node)
        return null;
    const nodeWithGetClientRect = node;
    if (typeof nodeWithGetClientRect.getClientRect === 'function') {
        return nodeWithGetClientRect.getClientRect({ skipStroke: true });
    }
    return null;
};
/**
 * Находит элемент-донор по координате снаппинга
 *
 * @param stage - Сцена Konva
 * @param lineGuide - Координата снаппинга (в экранных координатах)
 * @param orientation - Ориентация (V - вертикальная, H - горизонтальная)
 * @param targetNode - Узел, который исключается из поиска
 * @returns Bounding box элемента-донора или null
 */
const findDonorElement = (stage, lineGuide, orientation, targetNode) => {
    const visibleElements = getVisibleElementsOnScreenSync(stage);
    for (const element of visibleElements) {
        if (element === targetNode)
            continue;
        const box = getNodeClientRect(element);
        if (!box)
            continue;
        // Проверяем совпадение координаты в зависимости от ориентации
        const isMatch = orientation === 'V'
            ? Math.abs(box.x - lineGuide) < COORDINATE_TOLERANCE ||
                Math.abs(box.x + box.width - lineGuide) < COORDINATE_TOLERANCE ||
                Math.abs(box.x + box.width / 2 - lineGuide) < COORDINATE_TOLERANCE
            : Math.abs(box.y - lineGuide) < COORDINATE_TOLERANCE ||
                Math.abs(box.y + box.height - lineGuide) < COORDINATE_TOLERANCE ||
                Math.abs(box.y + box.height / 2 - lineGuide) < COORDINATE_TOLERANCE;
        if (isMatch) {
            return box;
        }
    }
    return null;
};
/**
 * Вычисляет точки снаппинга для одной ориентации (вертикальной или горизонтальной)
 *
 * @param lineGuideStops - Массив координат снаппинга сцены
 * @param itemSnappingEdges - Точки снаппинга перемещаемого элемента
 * @param orientation - Ориентация (V - вертикальная, H - горизонтальная)
 * @param snappingGuidelineOffset - Расстояние для обнаружения снаппинга
 * @param resolvedStage - Сцена Konva
 * @param targetBox - Bounding box перемещаемого элемента
 * @param targetNode - Узел перемещаемого элемента
 * @returns Массив результатов снаппинга
 */
const calculateSnappingResults = (lineGuideStops, itemSnappingEdges, orientation, snappingGuidelineOffset, resolvedStage, targetBox, targetNode) => {
    const results = [];
    for (const lineGuide of lineGuideStops) {
        for (const itemBound of itemSnappingEdges) {
            const diff = Math.abs(lineGuide - itemBound.guide);
            if (diff >= snappingGuidelineOffset)
                continue;
            // Находим элемент-донор
            const donorBox = findDonorElement(resolvedStage, lineGuide, orientation, targetNode);
            // Вычисляем координаты точек для построения линии
            const isVertical = orientation === 'V';
            const targetScreenCoord = isVertical
                ? targetBox.y + targetBox.height / 2
                : targetBox.x + targetBox.width / 2;
            const donorScreenCoord = donorBox
                ? isVertical
                    ? donorBox.y + donorBox.height / 2
                    : donorBox.x + donorBox.width / 2
                : targetScreenCoord;
            // Создаем точки в экранных координатах
            const screenRefPoint = isVertical
                ? { x: lineGuide, y: donorScreenCoord }
                : { x: donorScreenCoord, y: lineGuide };
            const screenTargetPoint = isVertical
                ? { x: lineGuide, y: targetScreenCoord }
                : { x: targetScreenCoord, y: lineGuide };
            // Преобразуем в мировые координаты
            const referencePoint = screenToWorld(resolvedStage, screenRefPoint.x, screenRefPoint.y);
            const targetWorldPoint = screenToWorld(resolvedStage, screenTargetPoint.x, screenTargetPoint.y);
            // Преобразуем размеры из экранных координат в мировые
            const scale = resolvedStage.scaleX() || 1;
            const donorSize = donorBox
                ? {
                    width: donorBox.width / scale,
                    height: donorBox.height / scale,
                }
                : undefined;
            const targetSize = {
                width: targetBox.width / scale,
                height: targetBox.height / scale,
            };
            results.push({
                lineGuide,
                diff,
                snap: itemBound.snap,
                offset: itemBound.offset,
                referencePoint,
                targetPoint: targetWorldPoint,
                donorSize,
                targetSize,
            });
        }
    }
    return results;
};
/**
 * Получает точки снаппинга для всех элементов на сцене
 *
 * @param stage - Сцена Konva
 * @param skipShape - Элемент или массив элементов, которые исключаются из снаппинга
 * @param snapToStageCenter - Если true, добавляет центр сцены в точки снаппинга
 * @param proximityRect - Прямоугольная зона для отбора элементов. Если указана,
 *   используются только элементы, пересекающие эту зону; anchorPoints и maxDistance
 *   не участвуют в фильтрации.
 * @returns Объект с вертикальными и горизонтальными точками снаппинга
 */
export const getLineGuideStops = (stage, skipShape, snapToStageCenter = false, proximityRect) => {
    if (!stage) {
        return { vertical: [], horizontal: [] };
    }
    const vertical = snapToStageCenter ? [stage.width() / 2] : [];
    const horizontal = snapToStageCenter ? [stage.height() / 2] : [];
    // Преобразуем skipShape в массив для удобства проверки
    const skipShapes = Array.isArray(skipShape) ? skipShape : [skipShape];
    const visibleElements = getVisibleElementsOnScreenSync(stage);
    for (const guideItem of visibleElements) {
        // Пропускаем элементы из списка исключений
        if (skipShapes.includes(guideItem))
            continue;
        const box = getNodeClientRect(guideItem);
        if (!box)
            continue;
        const rect = { x: box.x, y: box.y, width: box.width, height: box.height };
        // Если задана прямоугольная зона, ориентируемся только на пересечение с ней.
        // Старая логика отбора по anchorPoints/maxDistance намеренно удалена,
        // так как теперь поиск кандидатов базируется только на зоне перемещаемого элемента.
        if (proximityRect) {
            const intersects = rect.x < proximityRect.x + proximityRect.width &&
                rect.x + rect.width > proximityRect.x &&
                rect.y < proximityRect.y + proximityRect.height &&
                rect.y + rect.height > proximityRect.y;
            if (!intersects)
                continue;
        }
        // Добавляем все края и центр элемента в экранных координатах
        vertical.push(box.x, box.x + box.width, box.x + box.width / 2);
        horizontal.push(box.y, box.y + box.height, box.y + box.height / 2);
    }
    return { vertical, horizontal };
};
/**
 * Вычисляет общий bounding box для нескольких элементов
 *
 * @param nodes - Массив узлов Konva
 * @returns Общий bounding box или null, если узлы не найдены
 */
export const getMultipleElementsBoundingBox = (nodes) => {
    if (nodes.length === 0)
        return null;
    const boxes = [];
    for (const node of nodes) {
        const box = getNodeClientRect(node);
        if (box) {
            boxes.push(box);
        }
    }
    if (boxes.length === 0)
        return null;
    // Находим минимальные и максимальные координаты
    let minX = boxes[0].x;
    let minY = boxes[0].y;
    let maxX = boxes[0].x + boxes[0].width;
    let maxY = boxes[0].y + boxes[0].height;
    for (let i = 1; i < boxes.length; i++) {
        const box = boxes[i];
        minX = Math.min(minX, box.x);
        minY = Math.min(minY, box.y);
        maxX = Math.max(maxX, box.x + box.width);
        maxY = Math.max(maxY, box.y + box.height);
    }
    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
};
/**
 * Получает точки снаппинга для нескольких элементов на основе общего bounding box
 *
 * Для нескольких элементов используется общий bounding box от левого верхнего угла
 * до правого нижнего угла всех элементов. Снаппинг применяется к общему bounding box,
 * а все элементы перемещаются синхронно, сохраняя относительные позиции.
 *
 * @param nodes - Массив узлов Konva (первый элемент используется как опорный для offset)
 * @returns Объект с вертикальными и горизонтальными точками снаппинга
 */
export const getMultipleElementsSnappingEdges = (nodes) => {
    if (nodes.length === 0) {
        return { vertical: [], horizontal: [] };
    }
    const combinedBox = getMultipleElementsBoundingBox(nodes);
    if (!combinedBox) {
        // Если не удалось вычислить общий bounding box, используем первый элемент
        const firstNode = nodes[0];
        return getObjectSnappingEdges(firstNode);
    }
    // Используем первый узел как опорный для вычисления offset
    // offset нужен для корректного применения снаппинга через absolutePosition()
    const firstNode = nodes[0];
    const firstAbsPos = firstNode.absolutePosition();
    // Вычисляем offset для общего bounding box относительно первого элемента
    // offset - это разница между позицией первого узла и координатами общего bounding box
    const offsetX = firstAbsPos.x - combinedBox.x;
    const offsetY = firstAbsPos.y - combinedBox.y;
    return {
        vertical: [
            { guide: combinedBox.x, offset: offsetX, snap: 'start' },
            {
                guide: combinedBox.x + combinedBox.width / 2,
                offset: offsetX - combinedBox.width / 2,
                snap: 'center',
            },
            {
                guide: combinedBox.x + combinedBox.width,
                offset: offsetX - combinedBox.width,
                snap: 'end',
            },
        ],
        horizontal: [
            { guide: combinedBox.y, offset: offsetY, snap: 'start' },
            {
                guide: combinedBox.y + combinedBox.height / 2,
                offset: offsetY - combinedBox.height / 2,
                snap: 'center',
            },
            {
                guide: combinedBox.y + combinedBox.height,
                offset: offsetY - combinedBox.height,
                snap: 'end',
            },
        ],
    };
};
/**
 * Получает точки снаппинга для конкретного элемента
 *
 * ВАЖНО: Эта функция работает корректно с offset элементов, так как:
 * - getClientRect() возвращает координаты с учетом offset (реальные координаты элемента на экране)
 * - absolutePosition() возвращает позицию узла (с offset)
 * - Разница между ними автоматически учитывает offset элемента
 *
 * @param node - Элемент Konva
 * @returns Объект с вертикальными и горизонтальными точками снаппинга элемента
 */
export const getObjectSnappingEdges = (node) => {
    const box = getNodeClientRect(node);
    if (!box) {
        return { vertical: [], horizontal: [] };
    }
    const absPos = node.absolutePosition();
    // ВАЖНО: box.x и box.y - это реальные координаты левого верхнего угла элемента на экране
    // absPos - это позиция узла (с offset) в экранных координатах
    // offset для каждой точки - это разница между absPos и соответствующей точкой box
    return {
        vertical: [
            { guide: box.x, offset: absPos.x - box.x, snap: 'start' },
            { guide: box.x + box.width / 2, offset: absPos.x - box.x - box.width / 2, snap: 'center' },
            { guide: box.x + box.width, offset: absPos.x - box.x - box.width, snap: 'end' },
        ],
        horizontal: [
            { guide: box.y, offset: absPos.y - box.y, snap: 'start' },
            {
                guide: box.y + box.height / 2,
                offset: absPos.y - box.y - box.height / 2,
                snap: 'center',
            },
            { guide: box.y + box.height, offset: absPos.y - box.y - box.height, snap: 'end' },
        ],
    };
};
/**
 * Находит направляющие линии для снаппинга
 *
 * Функция сравнивает точки снаппинга сцены с точками снаппинга перемещаемого элемента
 * и создает направляющие линии для визуализации и применения снаппинга.
 *
 * @param params - Параметры для поиска направляющих линий
 * @returns Массив направляющих линий
 */
export const getGuides = ({ event, lineGuideStops, itemSnappingEdges, snappingDistance = 10, stage, linesNearDistance = 100, targetBox: providedTargetBox, }) => {
    var _a, _b, _c, _d;
    // Определяем расстояние для снаппинга (увеличиваем при зажатой Shift)
    const snappingGuidelineOffset = ((_a = event === null || event === void 0 ? void 0 : event.evt) === null || _a === void 0 ? void 0 : _a.shiftKey) ? snappingDistance * 20 : snappingDistance;
    const resolvedStage = (_d = stage !== null && stage !== void 0 ? stage : (_c = (_b = event === null || event === void 0 ? void 0 : event.target) === null || _b === void 0 ? void 0 : _b.getStage) === null || _c === void 0 ? void 0 : _c.call(_b)) !== null && _d !== void 0 ? _d : null;
    if (!resolvedStage)
        return [];
    const targetNode = event === null || event === void 0 ? void 0 : event.target;
    // Используем предоставленный targetBox или пытаемся получить его из targetNode
    const targetBox = providedTargetBox !== null && providedTargetBox !== void 0 ? providedTargetBox : getNodeClientRect(targetNode);
    if (!targetBox)
        return [];
    // Вычисляем результаты снаппинга для обеих ориентаций
    const resultV = calculateSnappingResults(lineGuideStops.vertical, itemSnappingEdges.vertical, 'V', snappingGuidelineOffset, resolvedStage, targetBox, targetNode);
    const resultH = calculateSnappingResults(lineGuideStops.horizontal, itemSnappingEdges.horizontal, 'H', snappingGuidelineOffset, resolvedStage, targetBox, targetNode);
    // Выбираем ближайшие направляющие
    const minV = resultV.sort((a, b) => a.diff - b.diff)[0];
    const minH = resultH.sort((a, b) => a.diff - b.diff)[0];
    const guides = [];
    // Создаем направляющую для вертикальной линии
    if (minV === null || minV === void 0 ? void 0 : minV.targetPoint) {
        const worldLineLength = linesNearDistance / (resolvedStage.scaleX() || 1);
        guides.push({
            lineGuide: minV.lineGuide,
            offset: minV.offset,
            orientation: 'V',
            snap: minV.snap,
            referencePoint: minV.referencePoint,
            coordinates: buildGuideLineCoordinates(minV.referencePoint, minV.targetPoint, 'V', worldLineLength, minV.snap, minV.donorSize),
        });
    }
    // Создаем направляющую для горизонтальной линии
    if (minH === null || minH === void 0 ? void 0 : minH.targetPoint) {
        const worldLineLength = linesNearDistance / (resolvedStage.scaleY() || 1);
        guides.push({
            lineGuide: minH.lineGuide,
            offset: minH.offset,
            orientation: 'H',
            snap: minH.snap,
            referencePoint: minH.referencePoint,
            coordinates: buildGuideLineCoordinates(minH.referencePoint, minH.targetPoint, 'H', worldLineLength, minH.snap, minH.donorSize),
        });
    }
    return guides;
};
/**
 * Отрисовывает направляющие линии на слое
 *
 * @param guides - Массив направляющих линий
 * @param layer - Слой Konva для отрисовки
 */
export const drawGuides = (guides, layer) => {
    for (const guide of guides) {
        const { startX, startY, endX, endY } = guide.coordinates;
        const line = new Konva.Line({
            points: [startX, startY, endX, endY],
            stroke: '#0156DE',
            strokeWidth: 0.5,
            strokeScaleEnabled: false,
            name: 'guid-line',
            dash: guide.orientation === 'H' ? [8, 11] : [8, 8],
            listening: false,
            zIndex: 1000,
            globalCompositeOperation: 'source-over',
        });
        layer.add(line);
    }
};
/**
 * Очищает все направляющие линии со слоя
 *
 * @param layer - Слой Konva
 */
export const clearGuides = (layer) => {
    layer.find('.guid-line').forEach((line) => line.destroy());
};
/**
 * Применяет снаппинг к позиции элемента
 *
 * ВАЖНО: Эта функция работает корректно с offset элементов, так как:
 * - absolutePosition() работает с позицией узла (с offset)
 * - Konva автоматически учитывает offset при установке absolutePosition()
 *
 * @param target - Целевой узел Konva
 * @param guides - Массив направляющих линий для снаппинга
 */
export const applySnapping = (target, guides) => {
    const absPos = target.absolutePosition();
    for (const guide of guides) {
        const snappedValue = guide.lineGuide + guide.offset;
        if (guide.orientation === 'V') {
            absPos.x = snappedValue;
        }
        else {
            absPos.y = snappedValue;
        }
    }
    target.absolutePosition(absPos);
};
/**
 * Вычисляет новую позицию общего bounding box на основе направляющих линий
 *
 * @param guides - Массив направляющих линий для снаппинга
 * @param combinedBox - Текущий общий bounding box (в экранных координатах)
 * @returns Новая позиция общего bounding box {x, y}
 */
const calculateSnappedBoundingBoxPosition = (guides, combinedBox) => {
    let newX = combinedBox.x;
    let newY = combinedBox.y;
    for (const guide of guides) {
        // guide.lineGuide - это координата снаппинга в экранных координатах
        // Для общего bounding box используем guide.lineGuide напрямую,
        // так как offset уже учтен при вычислении itemSnappingEdges
        if (guide.orientation === 'V') {
            // Вертикальная линия: снаппим по X
            switch (guide.snap) {
                case 'start':
                    // Снаппили по левому краю
                    newX = guide.lineGuide;
                    break;
                case 'end':
                    // Снаппили по правому краю
                    newX = guide.lineGuide - combinedBox.width;
                    break;
                case 'center':
                default:
                    // Снаппили по центру
                    newX = guide.lineGuide - combinedBox.width / 2;
                    break;
            }
        }
        else {
            // Горизонтальная линия: снаппим по Y
            switch (guide.snap) {
                case 'start':
                    // Снаппили по верхнему краю
                    newY = guide.lineGuide;
                    break;
                case 'end':
                    // Снаппили по нижнему краю
                    newY = guide.lineGuide - combinedBox.height;
                    break;
                case 'center':
                default:
                    // Снаппили по центру
                    newY = guide.lineGuide - combinedBox.height / 2;
                    break;
            }
        }
    }
    return { x: newX, y: newY };
};
/**
 * Применяет снаппинг к общему bounding box нескольких элементов
 *
 * Вычисляет смещение общего bounding box на основе guides и применяет
 * это смещение ко всем элементам одновременно, сохраняя их относительные позиции.
 *
 * ВАЖНО: Все элементы перемещаются синхронно с одинаковым смещением,
 * что обеспечивает сохранение их относительных позиций.
 *
 * @param nodes - Массив узлов Konva для применения снаппинга
 * @param guides - Массив направляющих линий для снаппинга
 * @param combinedBox - Общий bounding box всех элементов (в экранных координатах)
 */
export const applySnappingToMultipleElements = (nodes, guides, combinedBox) => {
    if (nodes.length === 0 || guides.length === 0)
        return;
    // Вычисляем новую позицию общего bounding box после снаппинга
    const newPosition = calculateSnappedBoundingBoxPosition(guides, combinedBox);
    // Вычисляем смещение общего bounding box
    const deltaX = newPosition.x - combinedBox.x;
    const deltaY = newPosition.y - combinedBox.y;
    // Применяем смещение ко всем элементам одновременно
    for (const node of nodes) {
        const currentAbsPos = node.absolutePosition();
        node.absolutePosition({
            x: currentAbsPos.x + deltaX,
            y: currentAbsPos.y + deltaY,
        });
    }
};
/**
 * Проверяет, находится ли перемещаемый узел внутри элемента с локальной сеткой
 *
 * Сравнение выполняется в экранных координатах.
 *
 * @param stage - Сцена Konva
 * @param target - Перемещаемый узел
 * @param boardState - Состояние доски
 * @returns true, если узел находится внутри элемента с локальной сеткой
 */
export function isInsideAnyLocalGrid(stage, target, boardState) {
    var _a, _b;
    const movingBox = getNodeClientRect(target);
    if (!movingBox)
        return false;
    const scale = stage.scaleX();
    const stageX = stage.x();
    const stageY = stage.y();
    // Получаем ID текущего узла для исключения самого себя
    const targetNode = target;
    const rawId = typeof targetNode.id === 'function' ? targetNode.id() : undefined;
    const selfId = rawId && typeof rawId === 'string' && rawId.startsWith('node-')
        ? Number(rawId.replace('node-', ''))
        : undefined;
    // Преобразует мировые координаты в экранные
    const toScreenRect = (x = 0, y = 0, w = 0, h = 0) => ({
        x: x * scale + stageX,
        y: y * scale + stageY,
        w: w * scale,
        h: h * scale,
    });
    // Проверяем пересечение с элементами, у которых включена локальная сетка
    for (const layer of boardState.layers || []) {
        for (const el of layer.elements || []) {
            if (!((_b = (_a = el === null || el === void 0 ? void 0 : el.props) === null || _a === void 0 ? void 0 : _a.localGrid) === null || _b === void 0 ? void 0 : _b.enabled))
                continue;
            if (selfId !== undefined && el.id === selfId)
                continue;
            const rect = toScreenRect(el.props.x, el.props.y, el.props.w, el.props.h);
            const intersects = movingBox.x < rect.x + rect.w &&
                movingBox.x + movingBox.width > rect.x &&
                movingBox.y < rect.y + rect.h &&
                movingBox.y + movingBox.height > rect.y;
            if (intersects)
                return true;
        }
    }
    return false;
}
/**
 * Находит трансформер из ref или по родительской цепочке события
 *
 * @param ref - Референс на трансформер
 * @param e - Событие мыши
 * @returns Найденный трансформер или null
 */
const findTransformerNode = (ref, e) => {
    var _a, _b, _c, _d;
    // Пытаемся получить трансформер из ref
    if (ref && 'current' in ref) {
        const transformer = ref.current;
        if (transformer)
            return transformer;
    }
    // Резервный поиск трансформера по родительской цепочке события
    const target = e === null || e === void 0 ? void 0 : e.target;
    if (!target)
        return null;
    let parent = target;
    while (parent) {
        const className = (_b = (_a = parent).getClassName) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (className === 'Transformer') {
            return parent;
        }
        parent = ((_d = (_c = parent).getParent) === null || _d === void 0 ? void 0 : _d.call(_c)) || null;
    }
    return null;
};
/**
 * Вычисляет ближайшую точку на линии снаппинга с учетом диагонального движения якоря при aspect lock
 *
 * При aspect lock якорь движется по диагонали (равномерно по обеим осям).
 * Эта функция находит точку пересечения диагонали движения якоря с линией снаппинга.
 *
 * @param oldPos - Старая позиция якоря (начало диагонали)
 * @param newPos - Новая позиция якоря (конец диагонали)
 * @param guideLine - Координата линии снаппинга
 * @param orientation - Ориентация линии ('V' - вертикальная, 'H' - горизонтальная)
 * @returns Ближайшая точка на линии снаппинга или null, если пересечения нет
 */
const findSnapPointOnDiagonal = (oldPos, newPos, guideLine, orientation) => {
    // Вычисляем направление движения якоря (диагональ)
    const deltaX = newPos.x - oldPos.x;
    const deltaY = newPos.y - oldPos.y;
    // Если якорь не двигается, возвращаем null
    if (Math.abs(deltaX) < 0.001 && Math.abs(deltaY) < 0.001) {
        return null;
    }
    if (orientation === 'V') {
        // Вертикальная линия: X фиксирован, находим Y на диагонали
        // Уравнение диагонали: y = oldPos.y + (deltaY / deltaX) * (x - oldPos.x)
        // При x = guideLine: y = oldPos.y + (deltaY / deltaX) * (guideLine - oldPos.x)
        if (Math.abs(deltaX) < 0.001) {
            // Якорь движется только по Y, проверяем, совпадает ли X с линией
            if (Math.abs(oldPos.x - guideLine) < 0.001) {
                return { x: guideLine, y: newPos.y };
            }
            return null;
        }
        const ratio = deltaY / deltaX;
        const snappedY = oldPos.y + ratio * (guideLine - oldPos.x);
        return { x: guideLine, y: snappedY };
    }
    else {
        // Горизонтальная линия: Y фиксирован, находим X на диагонали
        // Уравнение диагонали: x = oldPos.x + (deltaX / deltaY) * (y - oldPos.y)
        // При y = guideLine: x = oldPos.x + (deltaX / deltaY) * (guideLine - oldPos.y)
        if (Math.abs(deltaY) < 0.001) {
            // Якорь движется только по X, проверяем, совпадает ли Y с линией
            if (Math.abs(oldPos.y - guideLine) < 0.001) {
                return { x: newPos.x, y: guideLine };
            }
            return null;
        }
        const ratio = deltaX / deltaY;
        const snappedX = oldPos.x + ratio * (guideLine - oldPos.y);
        return { x: snappedX, y: guideLine };
    }
};
/**
 * Применяет снаппинг к позиции якоря трансформера
 *
 * Функция используется при перетаскивании якорей трансформера для привязки
 * к другим элементам на сцене. Поддерживает снаппинг для одного или нескольких элементов.
 *
 * ВАЖНО:
 * - Для нескольких элементов используется общий bounding box всех элементов,
 *   что обеспечивает корректный снаппинг всей группы при изменении размера.
 * - Используется реальная позиция якоря из e.target, а не позиция курсора (newPos),
 *   что критично для корректной работы при aspect lock, когда трансформер
 *   может изменить реальную позицию якоря относительно курсора.
 *
 * @param stage - Сцена Konva
 * @param ref - Референс на трансформер
 * @param e - Событие мыши
 * @param newPos - Новая позиция якоря (может быть скорректирована при aspect lock)
 * @param oldPos - Старая позиция якоря (реальная позиция якоря до перемещения)
 * @returns Снаппингнутая позиция или null, если снаппинг не применен
 */
export function getLineGuidedPositionForAnchorTransformer(stage, ref, e, newPos, oldPos) {
    var _a, _b, _c, _d;
    // Находим трансформер и его узлы
    const transformerNode = findTransformerNode(ref, e);
    if (!transformerNode)
        return null;
    // Проверяем, включен ли aspect lock (keepRatio)
    const keepRatio = (_c = (_b = (_a = transformerNode).keepRatio) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : false;
    const nodes = typeof transformerNode.nodes === 'function' ? transformerNode.nodes() : [];
    if (nodes.length === 0)
        return null;
    // Если нет oldPos, не можем вычислить диагональ для aspect lock
    if (keepRatio && !oldPos) {
        return null;
    }
    // ВАЖНО: Получаем реальную позицию якоря для снаппинга
    // При aspect lock трансформер может изменить позицию якоря относительно курсора,
    // поэтому используем oldPos (реальная позиция якоря) или пытаемся получить из e.target
    let anchorAbsolutePos;
    // Пытаемся получить реальную позицию якоря из e.target
    const anchorNode = e.target;
    if (anchorNode && typeof anchorNode.absolutePosition === 'function') {
        // Если e.target - это узел Konva с методом absolutePosition, используем его
        anchorAbsolutePos = anchorNode.absolutePosition();
    }
    else if (anchorNode && typeof anchorNode.getClientRect === 'function') {
        // Если есть метод getClientRect, используем его для получения позиции
        const rect = anchorNode.getClientRect({ skipStroke: true });
        anchorAbsolutePos = { x: rect.x, y: rect.y };
    }
    else if (oldPos) {
        // Используем oldPos (реальная позиция якоря до перемещения) как fallback
        anchorAbsolutePos = oldPos;
    }
    else {
        // Последний fallback: используем newPos
        anchorAbsolutePos = newPos;
    }
    // Преобразуем узлы в массив Shape для исключения из снаппинга
    const skipShapes = nodes.filter((node) => node !== undefined);
    // Получаем общий bounding box всех элементов трансформера
    const combinedBox = getMultipleElementsBoundingBox(nodes);
    if (!combinedBox)
        return null;
    // Строим прямоугольную зону поиска кандидатов снаппинга ВОКРУГ ЯКОРЯ,
    // а не вокруг центра общего bounding box.
    // Так расстояние до гайдлиний считается относительно точки якоря.
    const BASE_ZONE_MULTIPLIER = 2;
    const centerX = anchorAbsolutePos.x;
    const centerY = anchorAbsolutePos.y;
    const zoneWidth = combinedBox.width * BASE_ZONE_MULTIPLIER;
    const zoneHeight = combinedBox.height * BASE_ZONE_MULTIPLIER;
    const proximityRect = {
        x: centerX - zoneWidth / 2,
        y: centerY - zoneHeight / 2,
        width: zoneWidth,
        height: zoneHeight,
    };
    // Получаем точки снаппинга сцены, исключая все элементы трансформера
    const lineGuideStops = getLineGuideStops(stage, skipShapes.length > 1 ? skipShapes : skipShapes[0], false, proximityRect);
    // Эмулируем "ребра" объекта одной точкой (реальной позицией якоря в экранных координатах)
    // Используем реальную позицию якоря, а не newPos, для корректной работы при aspect lock
    const itemSnappingEdges = {
        vertical: [{ guide: Math.round(anchorAbsolutePos.x), offset: 0, snap: 'start' }],
        horizontal: [{ guide: Math.round(anchorAbsolutePos.y), offset: 0, snap: 'start' }],
    };
    // Получаем направляющие линии
    // Используем общий bounding box элементов трансформера как targetBox для правильного построения линий
    const guides = getGuides({
        event: e,
        lineGuideStops,
        itemSnappingEdges,
        snappingDistance: 5,
        stage,
        targetBox: combinedBox,
    });
    // Отрисовываем направляющие линии
    const resolvedLayer = skipShapes[0]
        ? skipShapes[0].getLayer()
        : (anchorNode === null || anchorNode === void 0 ? void 0 : anchorNode.getLayer()) || null;
    if (resolvedLayer) {
        clearGuides(resolvedLayer);
        if (guides.length > 0) {
            drawGuides(guides, resolvedLayer);
        }
        (_d = resolvedLayer.getStage()) === null || _d === void 0 ? void 0 : _d.batchDraw();
    }
    // Применяем снаппинг к позиции якоря
    if (guides.length === 0)
        return null;
    // Проверяем расстояние от курсора до якоря для возможности отклеивания
    // Если якорь приклеен к линии, но курсор отошел от якоря больше чем на UNSTICK_DISTANCE,
    // отклеиваем якорь (не применяем снаппинг)
    const UNSTICK_DISTANCE = 15; // Расстояние в пикселях, при котором якорь отклеивается от линии
    // Вычисляем расстояние между курсором (newPos) и реальной позицией якоря
    const distanceX = Math.abs(newPos.x - anchorAbsolutePos.x);
    const distanceY = Math.abs(newPos.y - anchorAbsolutePos.y);
    const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
    // Если курсор отошел от якоря слишком далеко, отклеиваем якорь от линии
    if (distance > UNSTICK_DISTANCE) {
        return null;
    }
    // Применяем снаппинг с учетом aspect lock
    if (keepRatio && oldPos) {
        // При aspect lock якорь движется по диагонали, нужно найти ближайшую точку на линии снаппинга
        // на этой диагонали
        const verticalGuide = guides.find((g) => g.orientation === 'V');
        const horizontalGuide = guides.find((g) => g.orientation === 'H');
        let bestSnapPoint = null;
        let minDistance = Infinity;
        // Проверяем вертикальную линию снаппинга
        if (verticalGuide) {
            const snapPoint = findSnapPointOnDiagonal(oldPos, newPos, verticalGuide.lineGuide + verticalGuide.offset, 'V');
            if (snapPoint) {
                const dist = Math.sqrt(Math.pow((snapPoint.x - newPos.x), 2) + Math.pow((snapPoint.y - newPos.y), 2));
                if (dist < minDistance) {
                    minDistance = dist;
                    bestSnapPoint = snapPoint;
                }
            }
        }
        // Проверяем горизонтальную линию снаппинга
        if (horizontalGuide) {
            const snapPoint = findSnapPointOnDiagonal(oldPos, newPos, horizontalGuide.lineGuide + horizontalGuide.offset, 'H');
            if (snapPoint) {
                const dist = Math.sqrt(Math.pow((snapPoint.x - newPos.x), 2) + Math.pow((snapPoint.y - newPos.y), 2));
                if (dist < minDistance) {
                    minDistance = dist;
                    bestSnapPoint = snapPoint;
                }
            }
        }
        // Если нашли точку на диагонали, применяем снаппинг
        if (bestSnapPoint) {
            return bestSnapPoint;
        }
        // Если не нашли точку на диагонали, не применяем снаппинг
        return null;
    }
    // Без aspect lock: применяем снаппинг по каждой оси независимо
    let snappedX = newPos.x;
    let snappedY = newPos.y;
    const verticalGuide = guides.find((g) => g.orientation === 'V');
    const horizontalGuide = guides.find((g) => g.orientation === 'H');
    // Применяем снаппинг по вертикали (X), если есть направляющая
    if (verticalGuide) {
        snappedX = verticalGuide.lineGuide + verticalGuide.offset;
    }
    // Применяем снаппинг по горизонтали (Y), если есть направляющая
    if (horizontalGuide) {
        snappedY = horizontalGuide.lineGuide + horizontalGuide.offset;
    }
    return { x: snappedX, y: snappedY };
}
